/**
 * API configuration for external SpreadSheet service
 * URL configurada en .env (REACT_APP_SPREADSHEET_API_URL)
 */

import { getIdToken } from 'src/services/authService';

const API_BASE = (process.env.REACT_APP_API_BASE_URL ?? '').replace(/\/$/, '');

export interface SpreadSheetApiConfig {
  baseUrl: string;
  endpoints: {
    import: string;
    export: string;
    importByProject: string;
  };
}

export interface ImportRowError {
  code: string;
  message: string;
  type: number;
}

export interface ImportRowResult {
  rowNumber: number;
  isValid: boolean;
  errors: ImportRowError[];
}

export interface ImportByProjectResult {
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  rowErrors: ImportRowResult[];
  hasErrors: boolean;
}

export interface UploadFileResponse {
  jobId: number;
  fileName: string;
  status: string;
  message: string;
}

export const spreadSheetApiConfig: SpreadSheetApiConfig = {
  baseUrl: API_BASE,
  endpoints: {
    import: `${API_BASE}/api/workload/forecast/import`,
    export: `${API_BASE}/api/Workload/forecast/export`,
    importByProject: `${API_BASE}/api/project/{id}/workload/forecast/import`,
  }
};

export async function importSpreadSheet(file: File): Promise<Response> {
  const idToken = await getIdToken();

  const formData = new FormData();
  formData.append('file', file);

  return fetch(spreadSheetApiConfig.endpoints.import, {
    method: 'POST',
    credentials: 'include',
    headers: {
      "Authorization": `Bearer ${idToken}`
    },
    body: formData
  });
}

export async function importSpreadSheetByProject(
  file: File,
  projectId: number | string
): Promise<UploadFileResponse> {
  const idToken = await getIdToken();

  const formData = new FormData();
  formData.append('file', file);

  const url = spreadSheetApiConfig.endpoints.importByProject.replace(
    '{id}',
    encodeURIComponent(String(projectId))
  );

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      "Authorization": `Bearer ${idToken}`
    },
    body: formData
  });

  if (!response.ok) {
    throw new Error(`Import failed with status ${response.status}`);
  }

  return response.json() as Promise<UploadFileResponse>;
}

export async function exportSpreadSheet(projectId?: number | string): Promise<UploadFileResponse> {
  const idToken = await getIdToken();

  const params = new URLSearchParams();
  if (projectId != null) params.append('ProjectId', String(projectId));
  const qs = params.toString();
  const url = `${spreadSheetApiConfig.endpoints.export}${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      "Accept": 'application/json',
      "Authorization": `Bearer ${idToken}`
    }
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  return response.json() as Promise<UploadFileResponse>;
}

export default spreadSheetApiConfig;
